import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Eye, Search, ShieldCheck, Download, Upload, Phone, Globe } from 'lucide-react';

import { API_URL } from '../../utils/api';
import { getErrorMessage } from '../../utils/errors';

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const promoteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to promote this user to Admin?")) return;
    try {
        await axios.put(`${API_URL}/admin/users/${userId}/promote`);
        toast.success("User promoted to Admin");
        fetchCustomers();
    } catch (error) {
        toast.error(getErrorMessage(error, "Failed to promote user"));
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/customers/export`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customers.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Customers exported successfully");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to export customers"));
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error("Please select a CSV file");
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/admin/customers/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(response.data.message);
      fetchCustomers();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to import customers"));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const getCustomerSource = (customer) => {
    if (customer.phone && !customer.email?.includes('@placeholder')) return 'phone';
    if (customer.email && customer.email !== '' && !customer.email.includes('@placeholder')) return 'web';
    return 'unknown';
  };

  const filteredCustomers = customers.filter(c => {
    const q = search.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-paper">
      <AdminSidebar />
      <div className="ml-64 p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-serif text-3xl font-bold text-ink mb-2">Customers</h1>
          <p className="text-ink/60 mb-8">View and manage all customers (web &amp; phone)</p>

          <div className="flex flex-wrap gap-4 mb-6 items-center">
            <div className="relative w-full sm:max-w-md flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                    aria-label="Search customers"
                    placeholder="Search name, email, or phone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                />
            </div>
            <div className="flex flex-wrap gap-2 sm:ml-auto">
              <Button variant="outline" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
              <input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                onChange={handleImport}
                className="hidden"
              />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing}>
                <Upload className="h-4 w-4 mr-2" /> {importing ? 'Importing...' : 'Import CSV'}
              </Button>
            </div>
          </div>

          <div className="text-sm text-ink/50 mb-4">
            Showing {filteredCustomers.length} of {customers.length} customers
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div></div>
          ) : (
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-ink/40">
                        {search ? 'No customers match your search' : 'No customers found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCustomers.map((customer) => {
                      const source = getCustomerSource(customer);
                      return (
                        <TableRow key={customer.id}>
                          <TableCell className="font-medium">{customer.name}</TableCell>
                          <TableCell className="text-sm">
                            {customer.email && !customer.email.includes('@placeholder') ? customer.email : <span className="text-ink/30">-</span>}
                          </TableCell>
                          <TableCell className="text-sm font-mono">
                            {customer.phone || <span className="text-ink/30">-</span>}
                          </TableCell>
                          <TableCell>
                            {source === 'phone' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                <Phone className="h-3 w-3" /> Phone
                              </span>
                            ) : source === 'web' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                <Globe className="h-3 w-3" /> Web
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Unknown</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${customer.is_admin ? 'bg-gold/20 text-gold' : 'bg-gray-100 text-gray-600'}`}>
                              {customer.is_admin ? 'Admin' : 'User'}
                            </span>
                          </TableCell>
                          <TableCell className="text-ink/60">{new Date(customer.created_at).toLocaleDateString()}</TableCell>
                          <TableCell className="text-right flex justify-end gap-2">
                            {!customer.is_admin && (
                                <Button size="sm" variant="outline" onClick={() => promoteUser(customer.id)} title="Promote to Admin">
                                    <ShieldCheck className="h-4 w-4 mr-1" /> Promote
                                </Button>
                            )}
                            <Link to={`/admin/customers/${customer.id}`}>
                              <Button size="sm" variant="ghost">
                                <Eye className="h-4 w-4 mr-1" /> View
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
